// P-E2 D1/D2/D8/D10 — the shared «Дополнительные выплаты» block. Controlled and stateless: the host
// owns the value and the total (the total is base + Σ, and the base lives in the host, not here).
// Rendered by CreateInvoiceModal (create) and EditDraftInvoiceModal (draft edit), one call signature.
//
// EXTRA_KINDS mirrors backend/src/utils/invoiceCreateValidation.js:30 (INVOICE_EXTRA_KINDS) — the
// frontend cannot import from the backend package, so the ids and their order live here a second time
// and CTE2-P3-KINDORDER pins this literal. The API returns the extras sorted ALPHABETICALLY
// (services/invoiceService.js:117 — .orderBy(kind)), never in the PM's order, so the server array is
// INDEXED by kind (fromApiExtras) and never iterated (D2).
// The note belongs to «Другое» alone (D8): the backend accepts a note on any kind, but export.csv
// exports only the `other` one — offering it nowhere else keeps that column complete for every row
// the product can create.
import { useId } from 'react';

const EXTRA_KINDS = ['vpn_proxy', 'referral', 'mentoring', 'amb_manager', 'other'];
const NOTE_KIND = 'other';
const MAX_NOTE = 500;
const LABELS = {
  vpn_proxy: 'VPN/Proxy',
  referral: 'Реферальная система',
  mentoring: 'Наставничество',
  amb_manager: 'Амб.менеджер',
  other: 'Другое',
};

// D4: the client mirror of moneyCents (invoiceCreateValidation.js:40-45) — the same regex and the same
// integer-cent rounding, so the total the form prints is the total the server stores. Exported because
// both hosts compute «Итого» from base + Σ and must not re-declare their own parser.
export const MONEY_RE = /^\d+(\.\d{1,2})?$/;
export const centsOf = (s) => Math.round(Number(s) * 100);

export const emptyExtras = () => ({
  amounts: Object.fromEntries(EXTRA_KINDS.map((k) => [k, ''])),
  note: '',
});

// Indexes the API array by kind (D2) — arrival order is alphabetical and is deliberately ignored.
export function fromApiExtras(rows) {
  const value = emptyExtras();
  for (const row of rows ?? []) {
    if (!EXTRA_KINDS.includes(row?.kind)) continue;
    value.amounts[row.kind] = String(row.amount ?? '');
    if (row.kind === NOTE_KIND) value.note = String(row.note ?? '');
  }
  return value;
}

// Maps EXTRA_KINDS, never the argument, so two outputs are comparable with JSON.stringify
// (the seed-vs-candidate compare of the draft editor's Save). An empty amount = kind not charged.
export function toApiExtras(value) {
  const out = [];
  for (const kind of EXTRA_KINDS) {
    const amount = String(value?.amounts?.[kind] ?? '').trim();
    if (amount === '') continue;
    out.push(kind === NOTE_KIND
      ? { kind, amount, note: String(value?.note ?? '').trim() }
      : { kind, amount });
  }
  return out;
}

// Only amounts that pass MONEY_RE are summed, so a half-typed «1.0» contributes nothing rather than a
// wrong number — the total is held to the same bar as the payload.
export function extrasTotalCents(value) {
  let sum = 0;
  for (const kind of EXTRA_KINDS) {
    const amount = String(value?.amounts?.[kind] ?? '').trim();
    if (MONEY_RE.test(amount)) sum += centsOf(amount);
  }
  return sum;
}

// Mirrors the server's set-level rules (invoiceCreateValidation.js:55-70), trim included: the server
// builds String(e.note ?? '').trim() and rejects an empty one, so a mirror that did not trim would
// leave a whitespace-only note as a live 400.
export function extrasIssues(value) {
  const issues = [];
  const note = String(value?.note ?? '').trim();
  for (const kind of EXTRA_KINDS) {
    const amount = String(value?.amounts?.[kind] ?? '').trim();
    if (amount === '') continue;
    if (!MONEY_RE.test(amount) || centsOf(amount) <= 0) {
      issues.push({ kind, message: 'Сумма — больше 0, максимум 2 знака после точки' });
    } else if (kind === NOTE_KIND && !note) {
      issues.push({ kind, message: 'Для «Другое» нужен текст' });
    }
  }
  if (note.length > MAX_NOTE) {
    issues.push({ kind: NOTE_KIND, message: `Комментарий — не длиннее ${MAX_NOTE} символов` });
  }
  return issues;
}

// D10: the block carries its own <style>, scoped under .invoice-extras — the idiom both hosting modals
// already use. It is not a lead-card surface, so nothing goes into lead-card.css, and the adherence
// lint rule does not read template literals, so these declarations cost zero warnings.
const extrasStyles = `
  .invoice-extras { margin-bottom: 14px; }
  .invoice-extras__title {
    font-size: 12px; font-weight: 500; color: #6b7280;
    text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px;
  }
  .invoice-extras__row {
    display: grid; grid-template-columns: 1fr 128px;
    gap: 6px 8px; align-items: center; margin-bottom: 8px;
  }
  .invoice-extras__label {
    font-size: 13px; color: #0f172a;
    text-transform: none; letter-spacing: 0; margin-bottom: 0;
  }
  .invoice-extras input[type="number"], .invoice-extras input[type="text"] {
    width: 100%; padding: 7px 10px; border: 1px solid #d1d5db; border-radius: 8px;
    background: #ffffff; color: #0f172a; font-size: 14px; outline: none; box-sizing: border-box;
  }
  .invoice-extras input[type="number"]:focus, .invoice-extras input[type="text"]:focus {
    border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }
  .invoice-extras input:disabled { background: #f8fafc; color: #94a3b8; }
  .invoice-extras__note { grid-column: 1 / -1; }
  .invoice-extras__hint { grid-column: 1 / -1; font-size: 11px; color: #dc2626; margin: 0; }
`;

/**
 * @param {object} props
 * @param {{amounts: Record<string,string>, note: string}} props.value - controlled value (emptyExtras()).
 * @param {Function} props.onChange - receives the whole next value.
 * @param {boolean} [props.disabled=false]
 */
export default function InvoiceExtrasEditor({ value, onChange, disabled = false }) {
  // K4: the block is mounted once per open form, and several forms can be open at once — ids are per mount.
  const uid = useId();
  const issues = extrasIssues(value);
  const setAmount = (kind, amount) => onChange({
    ...value,
    amounts: { ...value.amounts, [kind]: amount },
  });

  return (
    <div className="invoice-extras">
      <style>{extrasStyles}</style>
      <div className="invoice-extras__title">Дополнительные выплаты</div>
      {EXTRA_KINDS.map((kind) => {
        const hint = issues.find((i) => i.kind === kind)?.message;
        return (
          <div className="invoice-extras__row" key={kind}>
            <label className="invoice-extras__label" htmlFor={`extra-${kind}-${uid}`} title={LABELS[kind]}>{LABELS[kind]}</label>
            <input
              id={`extra-${kind}-${uid}`}
              type="number"
              step="0.01"
              min="0"
              value={value.amounts[kind]}
              disabled={disabled}
              onChange={(e) => setAmount(kind, e.target.value)}
            />
            {kind === NOTE_KIND && (
              <input
                className="invoice-extras__note"
                type="text"
                aria-label="За что допвыплата «Другое»"
                placeholder="За что"
                value={value.note}
                disabled={disabled}
                onChange={(e) => onChange({ ...value, note: e.target.value })}
              />
            )}
            {hint && <p className="invoice-extras__hint">{hint}</p>}
          </div>
        );
      })}
    </div>
  );
}
